/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState, type CSSProperties } from "react";
import {
  BarChart3,
  ChevronRight,
  Clock,
  Database,
  Pin,
  Star,
  Stethoscope,
  Telescope,
  Wallet,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { useChrome } from "../contexts/ChromeContext";

const DEFAULT_QUESTION = "Which referral sources convert best to a booked first session?";

const SUGGESTIONS = [
  { label: "Referral → booked, by source", icon: BarChart3, question: DEFAULT_QUESTION },
  { label: "Payer mix this quarter", icon: Wallet },
  { label: "Time to first session", icon: Clock },
  { label: "Provider utilization", icon: Stethoscope },
];

const CHART = [
  { label: "Children's Hosp.", pct: 68 },
  { label: "UCLA Health", pct: 61 },
  { label: "Sutter", pct: 54 },
  { label: "Memorial H.", pct: 47 },
  { label: "Self-referral", pct: 39 },
  { label: "Pediatric", pct: 33 },
];

const CATALOG = [
  { title: "Referral → booked, by source", meta: "Pinned · refreshed 2h ago", pinned: true },
  { title: "Payer mix this quarter", meta: "Pinned", pinned: true },
  { title: "Time to first session", meta: "Last run 1d ago", pinned: false },
  { title: "Provider utilization", meta: "Pinned", pinned: true },
];

export function Insights() {
  useChrome({ title: "My Insights" }, []);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  function ask(question?: string) {
    const value = (question ?? draft).trim();
    setOpen(value === "" ? DEFAULT_QUESTION : value);
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 pb-4">
      <header className="enter max-w-2xl">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Ask your data anything.
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          Plain-English questions become SQL against the Postgres semantic layer. Every answer opens
          with its query and lineage, and saves to the catalog.
        </p>
      </header>

      <div className="enter space-y-4" style={{ "--enter-delay": "70ms" } as CSSProperties}>
        <div className="glass flex items-center gap-3 rounded-3xl p-2 pl-5">
          <Telescope size={18} className="shrink-0 text-accent" />
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                ask();
              }
            }}
            placeholder="Which referral sources convert best to a booked first session?"
            className="min-w-0 flex-1 bg-transparent py-3 text-[15px] text-text-primary outline-none placeholder:text-text-tertiary"
            aria-label="Ask a question of your data"
          />
          <Button onClick={() => ask()} className="shrink-0">
            Ask
            <ChevronRight size={16} />
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map(({ label, icon: Icon, question }) => (
            <button
              key={label}
              type="button"
              onClick={() => ask(question ?? label)}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-elevated/60 px-3.5 py-1.5 text-[13px] text-text-secondary transition-colors hover:border-border-hover hover:text-text-primary"
            >
              <Icon size={13} className="text-accent" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <section className="enter space-y-3" style={{ "--enter-delay": "140ms" } as CSSProperties}>
        <SectionHeading>Insight catalog</SectionHeading>
        <div className="grid gap-3 sm:grid-cols-2">
          {CATALOG.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={() => ask(item.title)}
              className="lift border border-border bg-bg-elevated shadow-card flex items-center justify-between gap-3 rounded-2xl p-4 text-left"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-medium text-text-primary">
                  {item.pinned && (
                    <Star size={13} className="fill-mana-gold text-mana-gold" aria-hidden />
                  )}
                  {item.title}
                </span>
                <span className="mt-1 block text-[11px] uppercase tracking-[0.08em] text-text-tertiary">
                  {item.meta}
                </span>
              </span>
              <ChevronRight size={16} className="shrink-0 text-text-tertiary" />
            </button>
          ))}
        </div>
      </section>

      <Modal
        isOpen={open !== null}
        onClose={() => setOpen(null)}
        title={open ?? ""}
        size="full"
        headerActions={
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-border-hover hover:text-text-primary"
          >
            <Star size={13} />
            Bookmark
          </button>
        }
        footer={<LineageFooter />}
      >
        <ResultBody />
      </Modal>
    </div>
  );
}

function ResultBody() {
  return (
    <div className="space-y-5 px-6 py-5">
      <div className="inline-flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-accent">
        <span className="size-1.5 rounded-full bg-current" />
        agent · generated
      </div>
      <p className="text-[15px] leading-relaxed text-text-secondary">
        <b className="font-semibold text-text-primary">Children's Hospital Colorado</b> converts
        best — 68% of its referrals reach a booked first session, roughly twice self-referral.
        Health-system sources outperform direct channels across the board.
      </p>
      <Chart />
      <CodeBlock />
    </div>
  );
}

function LineageFooter() {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-6 py-3 text-[11px] text-text-tertiary">
      <Database size={12} />
      <span>
        lineage: <b className="font-semibold text-text-secondary">referrals</b> ⋈{" "}
        <b className="font-semibold text-text-secondary">care_episodes</b> · 11,204 rows · 240ms
      </span>
      <span className="ml-auto flex items-center gap-2">
        <FootButton>Edit SQL</FootButton>
        <FootButton icon={<Pin size={12} />}>Pin to board</FootButton>
      </span>
    </div>
  );
}

function Chart() {
  const width = 640;
  const height = 200;
  const base = height - 28;
  const max = 76;
  const step = (width - 24) / CHART.length;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-52 w-full"
      role="img"
      aria-label="Conversion rate by referral source"
    >
      <line x1={12} y1={base} x2={width - 12} y2={base} className="stroke-border" strokeWidth={1} />
      {CHART.map((bar, index) => {
        const barHeight = (bar.pct / max) * (base - 16);
        const x = 12 + index * step + 10;
        const barWidth = step - 28;
        const y = base - barHeight;
        const isLead = index === 0;
        return (
          <g key={bar.label}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx={5}
              className={isLead ? "fill-accent" : "fill-accent/45"}
            />
            <text
              x={x + barWidth / 2}
              y={y - 7}
              textAnchor="middle"
              className="fill-text-primary font-mono text-[10px] font-semibold"
            >
              {bar.pct}%
            </text>
            <text
              x={x + barWidth / 2}
              y={height - 8}
              textAnchor="middle"
              className="fill-text-tertiary font-mono text-[10px]"
            >
              {bar.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CodeBlock() {
  return (
    <pre className="overflow-x-auto rounded-2xl border border-border bg-bg-base/70 p-4 font-mono text-[12.5px] leading-relaxed text-text-secondary">
      <span className="text-text-tertiary">-- generated against the Postgres semantic layer</span>
      {"\n"}
      <Kw>SELECT</Kw> r.source,{"\n"}
      {"       "}
      <Fn>ROUND</Fn>(<Fn>COUNT</Fn>(*) <Kw>FILTER</Kw> (<Kw>WHERE</Kw> e.first_session_at{" "}
      <Kw>IS NOT NULL</Kw>){"\n"}
      {"             "}* 100.0 / <Fn>COUNT</Fn>(*), 0) <Kw>AS</Kw> conversion_pct{"\n"}
      <Kw>FROM</Kw> referrals r{"\n"}
      <Kw>LEFT JOIN</Kw> care_episodes e <Kw>ON</Kw> e.referral_id = r.id{"\n"}
      <Kw>WHERE</Kw> r.created_at &gt;= <Str>'2026-04-01'</Str>
      {"\n"}
      <Kw>GROUP BY</Kw> r.source <Kw>ORDER BY</Kw> conversion_pct <Kw>DESC</Kw>;
    </pre>
  );
}

function Kw({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold text-info">{children}</span>;
}

function Fn({ children }: { children: React.ReactNode }) {
  return <span className="text-success">{children}</span>;
}

function Str({ children }: { children: React.ReactNode }) {
  return <span className="text-warning">{children}</span>;
}

function FootButton({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 font-medium text-text-secondary transition-colors hover:border-border-hover hover:text-text-primary"
    >
      {icon}
      {children}
    </button>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[12px] font-medium uppercase tracking-[0.12em] text-text-tertiary">
        {children}
      </span>
      <span className="h-px flex-1 bg-gradient-to-r from-border to-transparent" />
    </div>
  );
}
